import { h, defineComponent, onUnmounted, ref, SetupContext } from 'vue';
import { pageSubtitle } from '@/ui/components';
import { isProd } from '@/ui/helpers/systemHelper';
import { getLocale, t } from '@/ui/i18n/i18n';
import { Button } from '@/ui/components/Button';
import { createHubspotForm, onFormReady, onFormSubmitted } from '@/services/hubspot';
import { Modal } from '@/ui/components/Modal';
import { useTask } from '@/ui/concerns';
import { get, updateOrganization } from '@/services/api/resources/organization/actions';
import { versioned } from '@/services/api';
import { user } from '@/ui/auth/auth';
import { organization } from '@/ui/auth/organization';

interface HubspotSendFormModalProps {
  active: boolean;
}

const FORM_ID_DEV: Record<string, string> = {
  de: '273992fe-946e-4388-9603-3bd369ecfbc2',
  en: '0d392f3f-db9e-4616-8bc7-1448b259a72e',
  es: '5cd06a57-3e97-4515-a765-99680423665d',
  fr: 'd74de8e8-df51-4e09-ad6d-d6e88be2a475',
  'pt-BR': '12f10c0b-e525-4c9e-896b-e7f5699171f4',
};

const FORM_ID_PRODUCTION: Record<string, string> = {
  de: '19b2179c-9076-4d65-b508-5e8f5539ddae',
  en: '6a6d13de-4a61-4f36-9977-f76b55b0c9f9',
  es: '19b2179c-9076-4d65-b508-5e8f5539ddae',
  fr: '19b2179c-9076-4d65-b508-5e8f5539ddae',
  'pt-BR': '19b2179c-9076-4d65-b508-5e8f5539ddae',
};

// @ts-ignore
export const HubspotSendFormModal = defineComponent<HubspotSendFormModalProps>({
  name: 'SendFormModal',
  props: ['active'],
  setup(props, context: SetupContext) {
    let organizationVersion!: string;
    let fileInput!: HTMLInputElement;

    const updateOrganizationTask = useTask(
      () => updateOrganization(
        organization.value.id,
        versioned({ ...organization.value, formConverted: true }, organizationVersion),
      ),
      () => {
        organization.value.formConverted = true;
        context.emit('form-converted', true);
      },
    );
    const formIds = isProd ? FORM_ID_PRODUCTION : FORM_ID_DEV;
    const formId = formIds[getLocale()] || formIds.en;
    const formHaveBeenSent = ref(false);

    const onFormSubmittedListener = (event: MessageEvent) => onFormSubmitted(() => {
      formHaveBeenSent.value = true;
      updateOrganizationTask.run();
      context.emit('form-submitted', true);
    }, event);

    const closeModal = () => {
      formHaveBeenSent.value = false;
      context.emit('close');
    };

    const onFakeSubmitBtnClick = () => {
      document.querySelector<HTMLDivElement>('.hs-form input[type="submit"]')!.click();
    };

    const insertFileLabel = (hubspotForm: HTMLElement) => {
      const label = document.createElement('label');
      const inputField = hubspotForm.querySelector<HTMLInputElement>('.hs-form_to_convert')!;
      label.innerText = 'File:';
      inputField.prepend(label);
    };

    const insertPlusSign = (hubspotForm: HTMLElement) => {
      const fieldFile = hubspotForm.querySelector<HTMLInputElement>('.hs-fieldtype-file')!;
      const fileLabel = fieldFile.querySelector('label:nth-child(2)')!;
      const plusSign = document.createElement('span');
      plusSign.innerText = '+';
      plusSign.className = 'send-form-modal-hubspot__plus';
      fileLabel.prepend(plusSign);
    };

    const onFormReadyListener = (event: MessageEvent) => onFormReady(() => {
      const hubspotForm: HTMLDivElement = document.querySelector<HTMLDivElement>('.hs-form')!;
      const fileContainerDiv: HTMLDivElement = hubspotForm.querySelector<HTMLDivElement>('.hs_form_to_convert')!;

      insertFileLabel(hubspotForm);
      insertPlusSign(hubspotForm);
      fileInput = hubspotForm.querySelector<HTMLInputElement>('input[type="file"]')!;
      fileInput.setAttribute('accept', '.xls,.csv,.xlsx,.pdf');
      fileInput.addEventListener('change', (e: Event) => {
        fileContainerDiv.querySelector('.hs-form-required')?.remove();
        const filename: string = (e.currentTarget as HTMLInputElement).value.split('\\').pop()!;

        fileContainerDiv.querySelector('label > span:nth-child(2)')!.innerHTML = filename;
        hubspotForm.querySelector<HTMLInputElement>('.send-form-modal-hubspot__plus')!.remove();
      });
    }, event);

    window.addEventListener('message', onFormSubmittedListener);

    window.addEventListener('message', onFormReadyListener);

    useTask(() => get(user.value.organization.id), organizationResponse => {
      organization.value = organizationResponse.data;
      organizationVersion = organizationResponse.version;
    }).run();

    onUnmounted(() => {
      window.removeEventListener('message', onFormSubmittedListener);

      window.removeEventListener('message', onFormReadyListener);
    });

    return function render() {
      createHubspotForm({
        portalId: process.env.VUE_APP_HUBSPOT_PORT_ID as string,
        formId,
        target: '.hubspot-upload-form',
      });

      const sentMessage = [
        pageSubtitle(t('convert-form.form-sent')),
        h('div', t('convert-form.form-sent-subtext')),
      ];

      const form = [
        pageSubtitle(t('convert-form.main-title')),
        h('div', t('convert-form.main-title-subtext')),
        h('div', { class: 'hubspot-upload-form' }),
      ];

      const actions = [
        formHaveBeenSent.value
          ? h(Button, {
            props: { btnClass: 'mr-2 has-text-weight-semibold' },
            on: { click: closeModal },
          }, 'Ok')
          : [
            h(Button, {
              props: { type: 'is-text', btnClass: 'mr-2 has-text-weight-semibold' },
              on: { click: closeModal },
            }, t('convert-form.cancel')),
            h(Button, {
              on: { click: onFakeSubmitBtnClick },
            }, t('convert-form.submit')),
          ],
      ];

      return h(Modal, {
        props: {
          active: props.active,
          trapFocus: true,
          destroyOnHide: false,
          ariaRole: 'dialog',
          ariaModal: true,
          customClass: 'send-form-modal-hubspot',
          width: '650px',
        },
        scopedSlots: {
          actions: () => actions,
        },
        on: {
          close: () => {
            formHaveBeenSent.value = false;
            context.emit('close');
          },
        },
      }, formHaveBeenSent.value ? [sentMessage] : [form]);
    };
  },
});
